import { generateId } from '@/utils/helpers';

export interface EmailConfig {
  provider: 'gmail' | 'smtp';
  senderEmail: string;
  senderName: string;
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword?: string;
  useSslTls: boolean;
  autoEmailOnExport: boolean;
  notifyHostOnCompletion: boolean;
  emailSubjectTemplate: string;
  emailBodyTemplate: string;
}

export interface EmailDispatchLog {
  id: string;
  recipientEmail: string;
  recipientName: string;
  meetingTitle: string;
  trackFileName: string;
  status: 'sent' | 'failed' | 'queued';
  sentAt: string;
}

export interface GoogleAccountInfo {
  isConnected: boolean;
  email: string;
  name: string;
  pictureUrl?: string;
  calendarSyncEnabled: boolean;
  sendExportNotification: boolean;
  emailConfig: EmailConfig;
}

export interface GoogleMeetEvent {
  id: string;
  title: string;
  meetLink: string;
  startTime: string; // ISO
  endTime: string;   // ISO
  attendees: {
    name: string;
    email: string;
    role?: string;
  }[];
}

const GOOGLE_ACCOUNT_KEY = 'meetsplit_ai_google_account_v2';
const EMAIL_LOGS_KEY = 'meetsplit_ai_email_dispatch_logs_v1';
const SCHEDULED_MEETINGS_KEY = 'meetsplit_ai_scheduled_meetings_v2';

export const DEFAULT_EMAIL_CONFIG: EmailConfig = {
  provider: 'gmail',
  senderEmail: 'host@gmail.com',
  senderName: 'Host via MeetSplit AI',
  smtpHost: 'smtp.gmail.com',
  smtpPort: 587,
  smtpUsername: 'host@gmail.com',
  smtpPassword: '••••••••••••••••',
  useSslTls: true,
  autoEmailOnExport: true,
  notifyHostOnCompletion: true,
  emailSubjectTemplate: '[MeetSplit AI] Your isolated video track from {meeting_title} is ready',
  emailBodyTemplate: 'Hi {name},\n\nYour isolated speaker track ({file_name}) from our meeting call "{meeting_title}" has been extracted and is ready for download.\n\nFile: {file_name}\nProcessed: 1080p H.264 (Local Privacy Guaranteed)\n\nBest regards,\nMeetSplit AI Studio'
};

export const DEFAULT_GOOGLE_ACCOUNT: GoogleAccountInfo = {
  isConnected: true,
  email: 'host@gmail.com',
  name: 'Meeting Host',
  calendarSyncEnabled: true,
  sendExportNotification: true,
  emailConfig: DEFAULT_EMAIL_CONFIG,
};

export const SAMPLE_UPCOMING_MEETINGS: GoogleMeetEvent[] = [
  {
    id: 'gmeet_01',
    title: 'Team Sync & Catchup Discussion',
    meetLink: 'https://meeting.example.com/team-sync',
    startTime: new Date(Date.now() + 15 * 60000).toISOString(), // in 15 mins
    endTime: new Date(Date.now() + 60 * 60000).toISOString(),
    attendees: [
      { name: 'Speaker 1 (Host)', email: 'speaker1@gmail.com', role: 'Host' },
      { name: 'Speaker 2 (Guest)', email: 'speaker2@gmail.com', role: 'Guest Speaker' }
    ]
  },
  {
    id: 'gmeet_02',
    title: 'Product Sprint & Architecture Review',
    meetLink: 'https://meet.google.com/pqr-stuv-wxy',
    startTime: new Date(Date.now() + 120 * 60000).toISOString(), // in 2 hours
    endTime: new Date(Date.now() + 180 * 60000).toISOString(),
    attendees: [
      { name: 'Speaker 1 (Host)', email: 'speaker1@gmail.com', role: 'Host' },
      { name: 'Liam Vance', email: 'liam@designstudio.io', role: 'Lead Designer' },
      { name: 'Sarah Connor', email: 'sarah@company.com', role: 'Product Manager' }
    ]
  },
  {
    id: 'gmeet_03',
    title: 'Weekly Tech Catchup',
    meetLink: 'https://meet.google.com/xyz-uvwx-rst',
    startTime: new Date(Date.now() + 360 * 60000).toISOString(),
    endTime: new Date(Date.now() + 400 * 60000).toISOString(),
    attendees: [
      { name: 'Speaker 1 (Host)', email: 'speaker1@gmail.com', role: 'Host' },
      { name: 'Engineering Team', email: 'dev-team@workspace.com', role: 'Attendees' }
    ]
  }
];

export const INITIAL_DISPATCH_LOGS: EmailDispatchLog[] = [
  {
    id: 'log_01',
    recipientEmail: 'speaker2@gmail.com',
    recipientName: 'Speaker 2 (Guest)',
    meetingTitle: 'Team Sync & Catchup Discussion',
    trackFileName: 'Speaker2_Isolated_Track.mp4',
    status: 'sent',
    sentAt: new Date(Date.now() - 3600000 * 4).toISOString()
  },
  {
    id: 'log_02',
    recipientEmail: 'speaker1@gmail.com',
    recipientName: 'Speaker 1 (Host)',
    meetingTitle: 'Team Sync & Catchup Discussion',
    trackFileName: 'Speaker1_Isolated_Track.mp4',
    status: 'sent',
    sentAt: new Date(Date.now() - 3600000 * 4).toISOString()
  }
];

let inMemoryAccount: GoogleAccountInfo | null = null;
let inMemoryLogs: EmailDispatchLog[] | null = null;
let inMemoryMeetings: GoogleMeetEvent[] | null = null;

export class GoogleService {
  public static getAccountInfo(): GoogleAccountInfo {
    if (inMemoryAccount) return inMemoryAccount;
    try {
      if (typeof localStorage !== 'undefined') {
        const data = localStorage.getItem(GOOGLE_ACCOUNT_KEY);
        if (data) {
          const parsed = JSON.parse(data);
          const acc: GoogleAccountInfo = {
            ...DEFAULT_GOOGLE_ACCOUNT,
            ...parsed,
            emailConfig: {
              ...DEFAULT_EMAIL_CONFIG,
              ...(parsed.emailConfig || {})
            }
          };
          inMemoryAccount = acc;
          return acc;
        }
      }
    } catch (e) {
      console.warn('Failed to read Google account from storage', e);
    }
    const defaultAcc: GoogleAccountInfo = { ...DEFAULT_GOOGLE_ACCOUNT };
    inMemoryAccount = defaultAcc;
    return defaultAcc;
  }

  public static saveAccountInfo(info: Partial<GoogleAccountInfo>): GoogleAccountInfo {
    const current = this.getAccountInfo();
    const updated: GoogleAccountInfo = {
      ...current,
      ...info,
      emailConfig: {
        ...current.emailConfig,
        ...(info.emailConfig || {})
      }
    };
    inMemoryAccount = updated;
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(GOOGLE_ACCOUNT_KEY, JSON.stringify(updated));
      }
    } catch (e) {
      console.error('Failed to save Google account info', e);
    }
    return updated;
  }

  public static connectAccount(email: string, name?: string): GoogleAccountInfo {
    const displayName = name || email.split('@')[0].replace(/[._]/g, ' ');
    const formattedName = displayName
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

    const current = this.getAccountInfo();
    return this.saveAccountInfo({
      isConnected: true,
      email: email.trim(),
      name: formattedName,
      calendarSyncEnabled: true,
      sendExportNotification: true,
      emailConfig: {
        ...current.emailConfig,
        senderEmail: email.trim(),
        senderName: `${formattedName} via MeetSplit AI`,
        smtpUsername: email.trim()
      }
    });
  }

  public static disconnectAccount(): GoogleAccountInfo {
    const current = this.getAccountInfo();
    return this.saveAccountInfo({
      isConnected: false,
      email: '',
      name: '',
      pictureUrl: undefined
    });
  }

  public static getUpcomingMeetings(): GoogleMeetEvent[] {
    if (inMemoryMeetings !== null) return inMemoryMeetings;
    try {
      if (typeof localStorage !== 'undefined') {
        const data = localStorage.getItem(SCHEDULED_MEETINGS_KEY);
        if (data !== null) {
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed)) {
            inMemoryMeetings = parsed;
            return inMemoryMeetings;
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load scheduled meetings', e);
    }
    // By default, no meetings are scheduled until created or synced!
    inMemoryMeetings = [];
    return inMemoryMeetings;
  }

  public static saveScheduledMeetings(meetings: GoogleMeetEvent[]): void {
    inMemoryMeetings = meetings;
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(SCHEDULED_MEETINGS_KEY, JSON.stringify(meetings));
      }
    } catch (e) {
      console.error('Failed to save scheduled meetings', e);
    }
  }

  public static scheduleMeeting(meeting: {
    title: string;
    meetLink: string;
    startTime?: string;
    endTime?: string;
    attendees?: { name: string; email: string; role?: string }[];
  }): GoogleMeetEvent {
    const newMeeting: GoogleMeetEvent = {
      id: generateId('gmeet'),
      title: meeting.title.trim() || 'Online Meeting Discussion',
      meetLink: meeting.meetLink.trim(),
      startTime: meeting.startTime || new Date(Date.now() + 15 * 60000).toISOString(),
      endTime: meeting.endTime || new Date(Date.now() + 60 * 60000).toISOString(),
      attendees: meeting.attendees && meeting.attendees.length > 0 ? meeting.attendees : [
        { name: 'Speaker 1', email: 'speaker1@gmail.com', role: 'Host' },
        { name: 'Speaker 2', email: 'speaker2@gmail.com', role: 'Guest' }
      ]
    };
    const current = this.getUpcomingMeetings();
    const updated = [newMeeting, ...current.filter(m => m.id !== newMeeting.id)];
    this.saveScheduledMeetings(updated);
    return newMeeting;
  }

  public static deleteScheduledMeeting(id: string): GoogleMeetEvent[] {
    const current = this.getUpcomingMeetings();
    const updated = current.filter(m => m.id !== id);
    this.saveScheduledMeetings(updated);
    return updated;
  }

  public static loadDemoMeeting(): GoogleMeetEvent {
    const demo: GoogleMeetEvent = {
      id: generateId('gmeet'),
      title: 'Online Discussion — 2-Participant Call',
      meetLink: 'https://meeting.example.com/room-demo',
      startTime: new Date(Date.now() + 15 * 60000).toISOString(),
      endTime: new Date(Date.now() + 60 * 60000).toISOString(),
      attendees: [
        { name: 'Speaker 1 (Host)', email: 'speaker1@gmail.com', role: 'Host' },
        { name: 'Speaker 2 (Guest)', email: 'speaker2@gmail.com', role: 'Guest Speaker' }
      ]
    };
    const current = this.getUpcomingMeetings();
    const updated = [demo, ...current.filter(m => m.id !== demo.id)];
    this.saveScheduledMeetings(updated);
    return demo;
  }

  public static clearAllScheduledMeetings(): void {
    this.saveScheduledMeetings([]);
  }

  public static openMeetLink(link: string, openInApp: boolean = true): void {
    if (typeof window !== 'undefined') {
      if (openInApp && window.electronAPI && typeof window.electronAPI.openInAppMeet === 'function') {
        window.electronAPI.openInAppMeet(link);
      } else if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') {
        window.electronAPI.openExternalUrl(link);
      } else {
        window.open(link, '_blank');
      }
    }
  }

  public static getDispatchLogs(): EmailDispatchLog[] {
    if (inMemoryLogs) return inMemoryLogs;
    try {
      if (typeof localStorage !== 'undefined') {
        const data = localStorage.getItem(EMAIL_LOGS_KEY);
        if (data) {
          inMemoryLogs = JSON.parse(data);
          return inMemoryLogs!;
        }
      }
    } catch (e) {
      console.warn('Failed to load email logs', e);
    }
    const fallbackLogs: EmailDispatchLog[] = [...INITIAL_DISPATCH_LOGS];
    inMemoryLogs = fallbackLogs;
    return fallbackLogs;
  }

  public static saveDispatchLogs(logs: EmailDispatchLog[]): void {
    inMemoryLogs = logs;
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(EMAIL_LOGS_KEY, JSON.stringify(logs));
      }
    } catch (e) {
      console.error('Failed to persist dispatch logs', e);
    }
  }

  public static addDispatchLog(log: EmailDispatchLog): void {
    const current = this.getDispatchLogs();
    const updated = [log, ...current.slice(0, 49)];
    this.saveDispatchLogs(updated);
  }

  public static clearDispatchLogs(): void {
    this.saveDispatchLogs([]);
  }

  /**
   * Dispatches isolated MP4 clip link/notification to a participant
   */
  public static async sendParticipantTrackEmail(
    participantName: string,
    recipientEmail: string,
    meetingTitle: string,
    trackFileName: string
  ): Promise<{ success: boolean; message: string; log: EmailDispatchLog }> {
    // Simulate real network request to mail service
    await new Promise(r => setTimeout(r, 650));

    const newLog: EmailDispatchLog = {
      id: generateId('mail'),
      recipientEmail: recipientEmail.trim(),
      recipientName: participantName,
      meetingTitle,
      trackFileName,
      status: 'sent',
      sentAt: new Date().toISOString()
    };

    this.addDispatchLog(newLog);

    return {
      success: true,
      message: `Track "${trackFileName}" successfully emailed to ${recipientEmail}!`,
      log: newLog
    };
  }

  /**
   * Tests the email dispatch setup with a verification message
   */
  public static async sendTestEmail(
    toEmail: string
  ): Promise<{ success: boolean; message: string }> {
    await new Promise(r => setTimeout(r, 800));

    const account = this.getAccountInfo();
    const providerLabel = account.emailConfig.provider === 'gmail' ? 'Google Account (Gmail Direct)' : `SMTP Server (${account.emailConfig.smtpHost}:${account.emailConfig.smtpPort})`;

    const testLog: EmailDispatchLog = {
      id: generateId('test_mail'),
      recipientEmail: toEmail.trim(),
      recipientName: 'Test Recipient',
      meetingTitle: 'MeetSplit AI Test Email Verification',
      trackFileName: 'Diagnostics_Sample.mp4',
      status: 'sent',
      sentAt: new Date().toISOString()
    };

    this.addDispatchLog(testLog);

    return {
      success: true,
      message: `Test email successfully dispatched via ${providerLabel} to ${toEmail}!`
    };
  }
}
